<template>
  <div class="row cmp-migration-upload">
    <div class="col">
      <ws-checkbox
        v-model="uploadReveal"
        :label="$t('cart.extra_settings.upload.label')"
      >
        <div class="label-text q-mt-sm">
          {{ $t('cart.extra_settings.upload.text') }}
          <ws-label-link v-if="config.migrationInfoUrl" info target="_blank" :href="config.migrationInfoUrl">
            {{ $t('cart.extra_settings.upload.link') }}
          </ws-label-link>
        </div>
      </ws-checkbox>

      <transition name="slide-fade-vertical">
        <div v-show="uploadReveal" class="row q-pl-lg q-py-sm">
          <div class="col q-mt-sm">
            <ws-uploader
              ref="wsUploader"
              :url="uploadUrl"
              multiple
              auto-upload
              hide-upload-btn
              square
              flat
              bodered
              field-name="invoice"
              :supported-file-types="Object.keys(allowedTypes)"
              :files="filesProcessed"
              :filter="filesFilter"
              :block-remove="loading"
              @remove-file="removeFile"
              @uploaded="processUploaded"
            />

            <ws-message
              type="alert"
              path="cart.migrationPromotion.uploadedFiles"
            />

            <ws-radio
              v-model="needAssistance"
              class="full-width flex items-center q-mt-lg"
              :color="isTheme(Theme.ADMIN) ? 'blue' : 'green'"
              :val="false"
            >
              <small>{{
                $t('cart.extra_settings.upload.no_assistance')
              }}</small>
            </ws-radio>
            <ws-radio
              v-model="needAssistance"
              class="full-width flex items-center q-mt-sm"
              :color="isTheme(Theme.ADMIN) ? 'blue' : 'green'"
              :val="true"
            >
              <small>{{
                $t('cart.extra_settings.upload.need_assistance')
              }}</small>
            </ws-radio>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script lang="ts">
// @ts-nocheck -- baseline for pre-existing errors so typecheck can be turned on
import {Component, Ref, Vue, Watch} from 'vue-property-decorator';
import { mapState } from 'vuex';

import { Throttle, byteConvert } from '@odin/utils';
import { get } from 'lodash-es';
import type { QuasarFile } from '@odin/ts/types/wsk_types';

import WsMessage from '../../../components/WsMessage.vue';
import { WsLabelLink, WsRadio, WsCheckbox, WsUploader} from '@odin/components-vue2';
import type { Cart, File, UploadedInvoice } from '@/types';
import { ensureCartLoaded } from '@/store';
import config, { type AppConfig } from '@/services/config.service';
import { Theme, useMessages } from '@odin/services';
import { StateMutations, StoreActions } from '@/store/const.enum';
import { setAssistance, fetchCart } from '@/services/cart/cart-api.service';

const {postMessage, handleUnstructuredError} = useMessages();

const UPLOAD_URL = '/promotion-for-migration';
const UPLOAD_FILES_PATH = 'migrationPromotion.uploadedFiles';
const MESSAGES_PATH = 'cart.' + UPLOAD_FILES_PATH;

const ALLOWED_TYPES = {
  'jpg': 'image/jpeg',
  // 'image/png',
  'pdf': 'application/pdf',
};
const ALLOWED_SIZE: { value: number; text: string } = {
  value: 5242880,
  text: '5MB',
};

@Component({
  components: {
    WsUploader,
    WsRadio,
    WsMessage,
    WsLabelLink,
    WsCheckbox,
  },
  computed: {
    ...mapState(['cart']),
  },
})
export default class MigrationUpload extends Vue {
  readonly cart!: Cart;
  @Ref('wsUploader') readonly wsUploader!: WsUploader;

  config: AppConfig = config;
  // null => no migration, false => do no need assistance with migration,
  // true => do need assistance
  needAssistance: boolean | null = null;
  uploadReveal = false;
  loading = false;
  uploadUrl = UPLOAD_URL;
  Theme = Theme;

  allowedTypes = ALLOWED_TYPES;

  created() {
    ensureCartLoaded(() => (
      this.uploadReveal = !!this.filesFromCart.length
    ));
    this.uploadUrl = this.config.apiUrl + UPLOAD_URL;
  }

  get filesFromCart(): UploadedInvoice[] {
    // keeping full reactivity
    return (
      (this.cart &&
        this.cart.migrationPromotion &&
        this.cart.migrationPromotion.uploadedFiles) ||
      []
    );
  }

  get filesProcessed(): any[] {
    return this.filesFromCart.map((file: any) => {
      // copying props for displaying purposes
      file.name = file.clientFileName || 'unknown file name';
      if (file.fileSize) {
        file.__sizeLabel = byteConvert(file.fileSize);
      }
      return file;
    });
  }

  filesFilter(files: any[]) {
    return files.filter(file => {
      let allowed = false;
      if (!this.isAllowedFileType(file.type)) {
        postMessage(MESSAGES_PATH, this.$t('ws_uploader.validation.file_type'));
      } else if (!this.isAllowedFileSize(file.size)) {
        postMessage(MESSAGES_PATH, this.$t('ws_uploader.validation.file_size', {limit: ALLOWED_SIZE.text}));
      } else {
        allowed = true;
      }
      return allowed;
    });
  }

  isAllowedFileType(type: string) {
    return Object.values(ALLOWED_TYPES).includes(type);
  }

  isAllowedFileSize(size: number) {
    return size < ALLOWED_SIZE.value;
  }

  processUploaded(info: any) {
    const xhr: XMLHttpRequest = info.xhr;
    let file: File;
    try {
      file = JSON.parse(xhr.response);
    } catch (error) {
      handleUnstructuredError(null, error, [], MESSAGES_PATH);
    }

    // update also cart in store

    this.$store.commit(StateMutations.SET_STATE, {
      path: MESSAGES_PATH,
      value: file,
    });

    // cart price condition changed (0 files -> 1 file), we need recalculated price
    if (this.filesFromCart.length === 1) {
      fetchCart();
    }
  }

  removeFile(file: QuasarFile | UploadedInvoice) {
    if (!(file as UploadedInvoice).clientFileName) {
      // is QuasarFile
      file =
        this.filesFromCart.find(
          (f: UploadedInvoice) => f.clientFileName === (file as QuasarFile).name
        ) || file;
    }
    this.$store
      .dispatch(StoreActions.REMOVE_FILE, {
        file,
        path: MESSAGES_PATH,
      })
      .then(() => this.onFileRemoved(file as any))
      .catch((err) => handleUnstructuredError(null, err, [], MESSAGES_PATH));
  }

  onFileRemoved(file: File) {
    // cart price condition changed (1 file -> 0 files), we need recalculated price
    if (this.filesFromCart.length === 0) {
      fetchCart();
    }

    this.wsUploader?.removeFile(file);
  }

  @Watch('cart.migrationPromotion.needAssistance', { immediate: true })
  onCartAssistanceChange(newVal: boolean) {
    if (this.needAssistance !== newVal) {
      this.needAssistance = newVal;
    }
  }

  @Watch('uploadReveal', { immediate: true })
  onRevealChange(newVal: boolean, oldVal: boolean|undefined) {
    let needAssistance = get(
      this.cart,
      'migrationPromotion.needAssistance',
      null
    );
    if (!newVal) {
      // on unchecking migration checkbox set null value
      needAssistance = null;

      // on unchecking migration checkbox remove files and reset the uploader
      // ignore the first immediate trigger of this watcher (when oldValue is undefined),
      // because that causes the file(s) to be removed upon page refresh
      if (oldVal !== undefined) {
        this.filesFromCart.forEach( fileFromCart => {
          if (!this.filesFromCart.length) {
            return;
          }
          this.removeFile(fileFromCart);
          this.wsUploader?.reset();
        });
      }

    } else if (needAssistance === null) {
      // on chacking migration checkbox set default value
      needAssistance = false;
    }
    // otherwise set actual state from cart if differs from already set
    if (this.needAssistance !== needAssistance) {
      this.needAssistance = needAssistance;
    }
  }

  @Watch('needAssistance')
  onAssistanceChange(newVal: boolean | null) {
    this.setAssistance(newVal);
  }

  @Throttle(500)
  setAssistance(newVal: boolean | null) {
    if (get(this.cart, 'migrationPromotion.applicable')) {
      setAssistance(newVal);
    }
  }

  destroyed() {
    // if leaving the screen without files, set assistance to null
    if (this.filesFromCart.length === 0) {
      this.setAssistance(null);
    }
  }
}
</script>

<style lang="scss" scoped>
.cmp-migration-upload {
  .label-text {
    color: $ws-base110;
    font-weight: normal;
  }

  .ws-uploader::v-deep > .q-uploader {
    // custom border, credits: https://kovart.github.io/dashed-border-generator/
    background-color: $ws-white;
    // background-image: url("data:image/svg+xml,%3csvg width='100%25'
    // height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect
    // width='100%25' height='100%25' fill='none' rx='6' ry='6'
    // stroke='%2397A1ABFF' stroke-width='2' stroke-dasharray='10%2c 5'
    // stroke-dashoffset='0' stroke-linecap='butt'/%3e%3c/svg%3e");
    background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='6' ry='6' stroke='%2397A1ABFF' stroke-width='2' stroke-dasharray='4%2c8' stroke-dashoffset='20' stroke-linecap='square'/%3e%3c/svg%3e");
    border-radius: $ws-border-radius !important;

    .q-uploader__list {
      background: none;

      > .list-content.row {
        background: none;
        border: none;
      }
    }

    .file-card.q-card .q-card__section {
      .ws-icon {
        color: $ws-base140;
      }
      .group-heading {
        font-family: $ws-font-family-secondary;
        font-size: 18px;
        line-height: 24px;
        color: $ws-base140;
      }

      p {
        font-family: $ws-font-family-generic;
        font-size: 14.4px;
        line-height: 22px;
        color: $ws-base100;

        .cta-link {
          text-decoration: none;
          border-bottom: 1px solid transparent;
          transition: border-bottom-color $element-hover-animation-speed linear;

          &:hover {
            border-bottom-color: $ws-link-color;
          }
        }
      }
    }
  }
}
</style>
